import React, { useRef, useState } from 'react';
import './JotBar.css';

/**
 * The jot bar — universal, pinned to the viewport bottom (phone) or the notes
 * column bottom (iPad+), and NEVER disabled (design handoff §Interactions).
 *
 * Self-replenishing: submit commits the note and immediately presents a fresh,
 * FOCUSED field — no re-tap. A teacher mid-lesson is writing something down
 * before they forget it. Submits on Enter and on the + button (both paths ≥44px).
 *
 * The first jot auto-starts a forgotten timer (onFirstJot → startIfIdle).
 */
const JotBar = ({
	onSubmit,
	onFirstJot = null,
	placeholder = 'Jot a note — yours until you deliver it',
	// Optional: report unsent text so it survives a route change. The field
	// stays UNCONTROLLED from the parent's point of view — `draft` seeds the
	// initial value only — because routing every keystroke through a parent
	// that also persists it is how a mid-lesson input starts dropping
	// characters. See useLessonScratch.
	draft = '',
	onDraftChange = null,
}) => {
	const [value, setValue] = useState(draft);
	const inputRef = useRef(null);

	const commit = async () => {
		const text = value.trim();
		if (!text) return;
		setValue('');
		if (onDraftChange) onDraftChange('');
		// Re-focus BEFORE the await: iOS only honors programmatic focus in the
		// gesture's synchronous window, and the optimistic insert means the field
		// is already free.
		inputRef.current?.focus();
		if (onFirstJot) onFirstJot();
		const accepted = await onSubmit(text);
		if (accepted === false) {
			// Rolled back — the hook restored its error message; put the text back
			// so nothing the teacher typed is lost.
			setValue((prev) => prev || text);
			if (onDraftChange) onDraftChange(text);
		}
	};

	return (
		<div className="ld-jotbar" data-guide="lesson-jot">
			<input
				ref={inputRef}
				className="ld-jotbar__field"
				type="text"
				value={value}
				placeholder={placeholder}
				enterKeyHint="send"
				onChange={(e) => {
					setValue(e.target.value);
					if (onDraftChange) onDraftChange(e.target.value);
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault();
						commit();
					}
				}}
			/>
			<button
				type="button"
				className="ld-jotbar__add"
				aria-label="Add note"
				onClick={commit}>
				<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" />
			</button>
		</div>
	);
};

export default JotBar;
